import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classNames from 'classnames';
import { Field, useForm, useFormState } from 'react-final-form';
import { RadioField, TextField, MultiSelectField } from '@unite-us/app-components';
import { Toggle } from 'common/Toggle';
import { useFind } from 'src/api/APIHooks';
import { useContactMethodChoices } from 'src/common/hooks';
import ContactHoursEdit from 'src/common/display/ContactHours/ContactHoursEdit';
import { required } from 'src/common/form/utils/rffValidations';

const HINT_TEXT = 'Notes appear throughout client records. Please avoid including sensitive information (including PHI) in your note.'; // eslint-disable-line max-len
const NOTE_PROMPT = 'Please ask the client if there are special instructions that should be followed when contacting them.'; // eslint-disable-line max-len

// Sub-component for Language Fields
function LanguageFields({ languages, isLoadingLanguages }) {
  // Transform languages to MultiSelectField's required format: {value, label}
  // Memoize to prevent options array recreation on every render
  const languageOptions = useMemo(
    () => languages.map((lang) => ({
      value: lang.name,
      label: lang.name,
    })),
    [languages],
  );

  return (
    <div className="w-6/12">
      <Field name="preferred_spoken_languages">
        {({ input, meta }) => (
          <MultiSelectField
            id="preferred-spoken-languages-field"
            label="Preferred Spoken Languages"
            value={input.value || []}
            onChange={(newValue) => {
                input.onChange(newValue);
              }}
            onBlur={input.onBlur}
            options={languageOptions}
            placeholder="Select..."
            clearable
            isLoading={isLoadingLanguages}
            error={meta.touched ? meta.error : undefined}
            menuPortalTarget={document.body}
          />
          )}
      </Field>
      <Field name="preferred_written_languages">
        {({ input, meta }) => (
          <MultiSelectField
            id="preferred-written-languages-field"
            label="Preferred Written Languages"
            value={input.value || []}
            onChange={(newValue) => {
                input.onChange(newValue);
              }}
            onBlur={input.onBlur}
            options={languageOptions}
            placeholder="Select..."
            clearable
            isLoading={isLoadingLanguages}
            error={meta.touched ? meta.error : undefined}
            menuPortalTarget={document.body}
          />
          )}
      </Field>
    </div>
  );
}

LanguageFields.propTypes = {
  languages: PropTypes.array.isRequired,
  isLoadingLanguages: PropTypes.bool.isRequired,
};

// Sub-component for Contact Method Fields
function ContactMethodFields({ requireContactPreferences, contactMethodChoices, isPhoneCallSelected }) {
  // Transform contact methods to MultiSelectField's required format: {value, label}
  // Memoize to prevent options array recreation on every render
  const methodOptions = useMemo(
    () => contactMethodChoices.map((method) => ({
      value: method.value,
      label: method.display_name,
    })),
    [contactMethodChoices],
  );

  return (
    <>
      <div className="w-6/12">
        <Field
          name="contact_preferences.preferred_contact_methods"
          validate={requireContactPreferences ? required : undefined}
        >
          {({ input, meta }) => (
            <MultiSelectField
              className="preferred-method-of-contact-field"
              id="preferred-method-of-contact-field"
              label="Methods of Contact"
              value={input.value || []}
              onChange={input.onChange}
              onBlur={input.onBlur}
              options={methodOptions}
              placeholder="Select..."
              clearable
              required={requireContactPreferences}
              error={meta.touched ? meta.error : undefined}
              menuPortalTarget={document.body}
            />
          )}
        </Field>
      </div>

      <Field
        name="contact_preferences.voice_mail_ok"
        validate={isPhoneCallSelected ? required : undefined}
      >
        {({ input, meta }) => (
          <RadioField
            className={classNames({ hidden: !isPhoneCallSelected })}
            id="leave-voicemail"
            value={input.value}
            onChange={input.onChange}
            onBlur={input.onBlur}
            label="Is it okay to leave voicemail?"
            options={[
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' },
            ]}
            inline
            required={isPhoneCallSelected}
            invalid={!!meta.error}
            touched={meta.touched}
            error={meta.touched && meta.error ? meta.error : undefined}
          />
        )}
      </Field>
    </>
  );
}

ContactMethodFields.propTypes = {
  requireContactPreferences: PropTypes.bool.isRequired,
  contactMethodChoices: PropTypes.array.isRequired,
  isPhoneCallSelected: PropTypes.bool.isRequired,
};

// Sub-component for Times To Contact Section
function TimesToContactSection({
  requireContactPreferences,
  timesToContactLabelClass,
  toggleSelectTimes,
  setToggleSelectTimes,
  clearTimeslots,
  submitFailed,
}) {
  const isTimeslotsValid = toggleSelectTimes && !submitFailed;
  const contactHoursClasses = classNames(
    'lg:w-2/3 px-6 pt-6 pb-2 border border-solid rounded',
    isTimeslotsValid ? 'border-dark-border-blue' : 'border-red',
  );

  return (
    <>
      <div className="lg:w-3/4 sm:w-auto">
        <label
          htmlFor="contact-requirements___selected-times-toggle"
          className={timesToContactLabelClass}
        >
          Times To Contact
        </label>
        <div className="grid grid-cols-6 mb-4">
          <Toggle
            checked={toggleSelectTimes}
            id="contact-requirements___selected-times-toggle"
            label="Selected Contact Times"
            labelClass="px-0 col-span-4 tracking-normal"
            onChange={() => {
              if (!requireContactPreferences) {
                const nextToggle = !toggleSelectTimes;
                setToggleSelectTimes(nextToggle);
                if (!nextToggle) {
                  clearTimeslots();
                }
              }
            }}
            toggleClass="px-0 pr-24 justify-self-end col-start-5 col-end-7"
            value={toggleSelectTimes}
          >
            <div className="pl-2">
              {toggleSelectTimes ? 'On' : 'Off'}
            </div>
          </Toggle>
        </div>
      </div>

      {toggleSelectTimes && (
        <>
          <div className={contactHoursClasses}>
            <ContactHoursEdit />
          </div>
        </>
      )}
    </>
  );
}

TimesToContactSection.propTypes = {
  requireContactPreferences: PropTypes.bool.isRequired,
  timesToContactLabelClass: PropTypes.string.isRequired,
  toggleSelectTimes: PropTypes.bool.isRequired,
  setToggleSelectTimes: PropTypes.func.isRequired,
  clearTimeslots: PropTypes.func.isRequired,
  submitFailed: PropTypes.bool.isRequired,
};

const ContactRequirementsForm = ({
  requireContactPreferences,
  onLanguagesLoaded,
}) => {
  const { values } = useFormState({ subscription: { values: true, submitFailed: true } });
  const form = useForm();
  const submitFailed = useFormState({ subscription: { submitFailed: true } }).submitFailed;
  const buildTimesToContactLabelClass = () => classNames(
    'ui-form-field__label w-full mb-2',
    requireContactPreferences ? 'ui-form-field__label--required' : '',
  );

  const [toggleSelectTimes, setToggleSelectTimes] = useState(requireContactPreferences);
  const [timesToContactLabelClass, setTimesToContactLabelClass] = useState(buildTimesToContactLabelClass);

  const languagesQuery = useFind(
    'languages',
    {},
    {
      queryConfig: {
        placeholderData: undefined,
      },
      sort: 'name',
    },
  );
  const { data: languagesData, isFetching: isLoadingLanguages } = languagesQuery;
  const languages = _.get(languagesData, 'data.data', []);

  const { contactMethodChoices } = useContactMethodChoices();

  const methodsSelected = _.get(values, 'contact_preferences.preferred_contact_methods', []);
  const isPhoneCallSelected = _.includes(methodsSelected, 'phone');

  const clearTimeslots = () => {
    form.change('contact_preferences.timeslots', {});
  };

  useEffect(() => {
    setToggleSelectTimes(requireContactPreferences);
    setTimesToContactLabelClass(buildTimesToContactLabelClass());
  }, [requireContactPreferences]);

  // Notify parent when languages are loaded
  useEffect(() => {
    if (languages.length > 0 && onLanguagesLoaded) {
      onLanguagesLoaded(languages);
    }
  }, [languages.length]); // Only re-run when the count changes, not the array reference

  return (
    <div className="mt-4 contact-requirements">
      <h2 className="contact-requirements__title">Contact Requirements</h2>

      <hr className="mt-1 mb-4" />

      <LanguageFields
        languages={languages}
        isLoadingLanguages={isLoadingLanguages}
      />

      <ContactMethodFields
        requireContactPreferences={requireContactPreferences}
        contactMethodChoices={contactMethodChoices}
        isPhoneCallSelected={isPhoneCallSelected}
      />

      <TimesToContactSection
        requireContactPreferences={requireContactPreferences}
        timesToContactLabelClass={timesToContactLabelClass}
        toggleSelectTimes={toggleSelectTimes}
        setToggleSelectTimes={setToggleSelectTimes}
        clearTimeslots={clearTimeslots}
        submitFailed={submitFailed}
      />

      <div className="mt-6 w-full">
        <Field name="contact_preferences.contact_notes">
          {({ input }) => (
            <TextField
              {...input}
              afterLabelContent={NOTE_PROMPT}
              hint={HINT_TEXT}
              id="contact-requirements__notes"
              label="Contact Notes"
            />
          )}
        </Field>
      </div>
    </div>
  );
};

ContactRequirementsForm.propTypes = {
  requireContactPreferences: PropTypes.bool,
  onLanguagesLoaded: PropTypes.func,
};

ContactRequirementsForm.defaultProps = {
  requireContactPreferences: false,
  onLanguagesLoaded: undefined,
};

ContactRequirementsForm.fields = [
  'preferred_spoken_languages',
  'preferred_written_languages',
  'contact_preferences.preferred_contact_methods',
  'contact_preferences.contact_notes',
  'contact_preferences.voice_mail_ok',
  'contact_preferences.timeslots.monday[].start_time',
  'contact_preferences.timeslots.monday[].end_time',
  'contact_preferences.timeslots.tuesday[].start_time',
  'contact_preferences.timeslots.tuesday[].end_time',
  'contact_preferences.timeslots.wednesday[].start_time',
  'contact_preferences.timeslots.wednesday[].end_time',
  'contact_preferences.timeslots.thursday[].start_time',
  'contact_preferences.timeslots.thursday[].end_time',
  'contact_preferences.timeslots.friday[].start_time',
  'contact_preferences.timeslots.friday[].end_time',
  'contact_preferences.timeslots.saturday[].start_time',
  'contact_preferences.timeslots.saturday[].end_time',
  'contact_preferences.timeslots.sunday[].start_time',
  'contact_preferences.timeslots.sunday[].end_time',
];

export default ContactRequirementsForm;
